import {Accordion} from "@/components/accordion/Accordion.tsx";
import {Card} from "@/components/card/Card.tsx";
import {Divider} from "@/components/divider/Divider.tsx";
import {TooltipIconRA} from "@/components/overlay/TooltipIconRA.tsx";
import {SAMLSettings} from "@/lib/types/statuspages.ts";

import {SAMLConfigLine} from "./SAMLConfigLine.tsx";

type SSOSetupStep1V2Props = {
  metadataURL: string;
  samlSettings: SAMLSettings;
};

export const SSOSetupStep1V2 = ({metadataURL, samlSettings}: SSOSetupStep1V2Props) => (
  <div className={"d-flex flex-column gap-4"}>
    <div className={"d-flex flex-column gap-2"}>
      <div className="pulseTextBodyBold">SAML Configuration</div>
      <span className="pulseTextBodySmall pulse-color-gray-600">
        Configure SAML SSO for your organization. Need Help?{" "}
        <a
          target="_blank"
          href="https://support.uptime.com/hc/en-us/articles/360001434205-Troubleshooting-Single-Sign-On-SSO-"
        >
          See our SAML SSO support article.
        </a>
      </span>
    </div>

    <Divider className={"my-2"} />

    <div className={"d-flex flex-column gap-2"}>
      <div className="pulseTextBodyBold">Service Provider Details</div>
      <span className="pulseTextBodySmall pulse-color-gray-600">
        Register these values in your identity provider. Or{" "}
        <a href={metadataURL} download>
          download metadata XML
        </a>
        .
      </span>
    </div>

    <SAMLConfigLine
      text="EntityID / Audience URL"
      tooltip="This is the value you'll need to enter in the 'Entity ID' field in your Identity Provider's configuration. This may also be referred to as 'Audience Restriction' or 'Audience URI'."
      code={samlSettings.sp_entity_id}
      variant="tab"
    />
    <SAMLConfigLine
      text="ACS URL / Consumer URL / Recipient"
      tooltip="This is the URL is where your Identity Provider will send SAML assertions, and will be required in your Identity Provider's admin when setting up an Uptime.com app."
      code={samlSettings.sp_acs_url}
      variant="tab"
    />
    <SAMLConfigLine
      text="WAYFless URL (optional)"
      tooltip="A URL that can be used to directly login to this Uptime.com account via your identity provider."
      code={samlSettings.sp_wayfless_url}
      variant="tab"
    />

    <Card>
      <Accordion>
        <Accordion.Item eventKey="0">
          <Accordion.Header>
            <div className="d-flex flex-column gap-1">
              <div className={"d-flex align-items-center gap-1"}>
                <span className="pulseTextBodySmallBold">Required SAML Attributes</span>
                <TooltipIconRA title="Attributes that must be sent by your identity provider to identify the user being authenticated." />
              </div>
            </div>
          </Accordion.Header>
          <Accordion.Body>
            <div className="d-flex flex-column mt-3 gap-3 pulseTextBodySmall">
              <span className="pulseTextBodySmall">These attributes must be set to authenticate the user:</span>
              <Divider />
              <div>
                Unique User Identifier or Email Address:{" "}
                <span className="pulseTextBodySmallBold">Name ID / Subject NameID</span>
              </div>
              <div>
                User's Email:{" "}
                <span className="pulseTextBodySmallBold">
                  Email / User.Email / E-Mail Address / Mail / eduPersonPrincipalName
                </span>
              </div>
              <div>
                User's First Name:{" "}
                <span className="pulseTextBodySmallBold">FirstName / User.FirstName / Given Name / givenName</span>
              </div>
              <div>
                User's Last Name:{" "}
                <span className="pulseTextBodySmallBold">LastName / User.LastName / Surname / sn</span>
              </div>
            </div>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </Card>

    <Card>
      <Accordion>
        <Accordion.Item eventKey="0">
          <Accordion.Header>
            <span className="pulseTextBodySmallBold">ActiveDirectory Setup Tips</span>
          </Accordion.Header>
          <Accordion.Body>
            <div className="d-flex flex-column mt-3 gap-3 pulseTextBodySmall">
              <Divider />
              <ol className="pulseTextBodySmallMedium d-flex flex-column gap-3">
                <li>
                  Download the Uptime.com Service Provider SAML Metadata from the link near the top of the page, and
                  import it into ActiveDirectory.
                </li>
                <li>Edit the Claim Rules for Uptime.com and add a new rule.</li>
                <li>
                  Map the following LDAP attributes to outgoing claim types:
                  <ol className="pulseTextBodySmall mt-2 p-0">
                    <li>E-Mail-Addresses &gt;&gt; Name ID</li>
                    <li>E-Mail-Addresses &gt;&gt; E-Mail Address</li>
                    <li>Given-Name &gt;&gt; Given Name</li>
                    <li>Surname &gt;&gt; Surname</li>
                  </ol>
                </li>
                <li>Fill in the Uptime.com SSO Setup form below with the appropriate ActiveDirectory details.</li>
              </ol>
            </div>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </Card>
  </div>
);
